import { formatDate, formatMoney, formatNumber } from '@platform/design-system';
import type { TFunction } from 'i18next';
import type { FieldValue, HistoryEntry } from './history.ts';

export type ValueContext = {
  readonly t: TFunction;
  readonly locale: string;
  readonly currency: string | null;
  readonly names: ReadonlyMap<string, string>;
};

type PresentValue = NonNullable<FieldValue>;
type Formatter = (value: PresentValue, context: ValueContext) => string;

const asText: Formatter = (value) => String(value);

const asMoney: Formatter = (value, { locale, currency }) =>
  currency === null ? String(value) : formatMoney({ amount: String(value), currency }, locale);

const asDay: Formatter = (value, { locale }) => formatDate(String(value), locale);

const asName: Formatter = (value, { t, names }) => names.get(String(value)) ?? t('history.value_other');

const asStatus: Formatter = (value, { t }) =>
  t(`history.status_${String(value)}`, { defaultValue: String(value) });

const asPreset: Formatter = (value, { t }) => t(`team.preset_${String(value)}`);

const asYesNo: Formatter = (value, { t }) => t(value === true ? 'history.value_yes' : 'history.value_no');

const asHours: Formatter = (value, { t }) => t('orderSettings.hoursLong', { count: Number(value) });

const asCount: Formatter = (value, { locale }) => formatNumber(Number(value), locale);

const FIELD_FORMATS: Readonly<Record<string, Formatter>> = {
  price: asMoney,
  compareAtPrice: asMoney,
  amount: asMoney,
  spentOn: asDay,
  subsidiaryId: asName,
  categoryId: asName,
  parentId: asName,
  status: asStatus,
  preset: asPreset,
  archived: asYesNo,
  isDefault: asYesNo,
  orderConfirmationTtlHours: asHours,
  variantCount: asCount,
};

const LTR_FIELDS: ReadonlySet<string> = new Set(['reference', 'code', 'phone', 'taxId']);

const CREATION_STEPS: ReadonlySet<string> = new Set(['created', 'invited']);

export const isCreation = (action: string): boolean => CREATION_STEPS.has(action.split('.').at(-1) ?? '');

export const isLtrField = (field: string): boolean => LTR_FIELDS.has(field);

export const formatFieldValue = (field: string, value: FieldValue, context: ValueContext): string =>
  value === null ? context.t('history.value_none') : (FIELD_FORMATS[field] ?? asText)(value, context);

export const fieldLabelOf = (field: string, t: TFunction): string =>
  t(`history.field_${field}`, { defaultValue: t('history.field_other') });

export const actorNameOf = (actor: HistoryEntry['actor'], t: TFunction): string =>
  actor.kind === 'person' && actor.name !== null ? actor.name : t(`history.actor_${actor.kind}`);

export const sentenceOf = (entry: HistoryEntry, t: TFunction): string => {
  const subject = entry.label ?? t('history.subject_unnamed');
  return t(`history.action_${entry.action.replaceAll('.', '_')}`, {
    subject,
    defaultValue: t('history.action_other', { subject }),
  });
};
